import React, { createContext, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { apiFunction } from "../../apiCalls/function";
import { MY_SECTIONS } from "../../apiCalls/urls/admin/access";
import { officerHomePath } from "./officers";

/** Officer-app sections an admin can switch on or off (see Officer Section Access). */
export const SECTIONS = {
    SCHOOL_VC_REPORT: "SCHOOL_VC_REPORT",
    SCHOOL_UPLOAD: "SCHOOL_UPLOAD",
    VC_UPLOAD: "VC_UPLOAD",
    CATEGORY_REPORT: "CATEGORY_REPORT",
};

const AccessContext = createContext({ enabled: false, loaded: true, can: () => true });

/**
 * Loads the signed-in officer's allowed sections once. With `enabled` false
 * (call-centre staff) everything is allowed and nothing is fetched.
 */
export const OfficerAccessProvider = ({ enabled, children }) => {
    const [allowed, setAllowed] = useState(null);

    useEffect(() => {
        if (!enabled) return;
        let alive = true;
        apiFunction(MY_SECTIONS, "get", null, true, "admin").then((res) => {
            if (!alive) return;
            const list = res?.status ? res.data?.sections || [] : [];
            setAllowed(new Set(list.filter((s) => s.allowed).map((s) => s.key)));
        });
        return () => { alive = false; };
    }, [enabled]);

    const value = enabled
        ? { enabled: true, loaded: allowed !== null, can: (key) => Boolean(allowed && allowed.has(key)) }
        : { enabled: false, loaded: true, can: () => true };

    return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
};

export const useOfficerAccess = () => useContext(AccessContext);

/** Renders the page only if the officer may open `section`; otherwise explains why not. */
export const SectionGate = ({ section, children }) => {
    const { enabled, loaded, can } = useOfficerAccess();
    const auth = useSelector((state) => state.Auth?.auth);
    if (!enabled) return children;
    if (!loaded) return <div className="ofc-page" />;
    if (can(section)) return children;
    return (
        <div className="ofc-page">
            <div className="ofc-card ofc-empty ofc-denied">
                <i className="fa fa-lock" />
                <strong>You don't have access to this section</strong>
                <span>Access to this section is managed by the administrator. Contact them if you need it.</span>
                <Link className="ofc-btn ofc-btn--ghost" to={officerHomePath(auth)}>Go to Home</Link>
            </div>
        </div>
    );
};

/** Wraps a route component in a SectionGate. */
export const gated = (Component, section) => {
    const Gated = (props) => (
        <SectionGate section={section}>
            <Component {...props} />
        </SectionGate>
    );
    return Gated;
};
